import { useEffect, useState, useCallback } from 'react';
import { format, addWeeks, subWeeks, eachDayOfInterval, isSameDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamTime, toAmsterdamDate, weekBounds } from '../../utils/dateUtils.js';
import LesDetailModal from './LesDetailModal.jsx';

export default function RoosterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess } = useToast();
  const [weekBase, setWeekBase] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [typeFilter, setTypeFilter] = useState(location.state?.filterType === 'group' ? 'group' : 'all');
  const [trialClient, setTrialClient] = useState(
    location.state?.trialClientId
      ? { id: location.state.trialClientId, name: location.state.trialClientName }
      : null
  );

  const { start, end } = weekBounds(weekBase);

  const loadLessons = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const startISO = start.toISOString();
      const endISO = new Date(end.getTime() + 24 * 3600 * 1000).toISOString();

      const { data: lessonsData, error: lessonsErr } = await supabase
        .from('group_lessons')
        .select(`
          id, name, description, starts_at, duration_minutes,
          max_participants, credit_cost, is_cancelled, type, recurrence_id,
          coach_id, executing_coach_id,
          locations(name, is_active),
          coach:profiles!group_lessons_coach_id_fkey(full_name),
          executing_coach:profiles!group_lessons_executing_coach_id_fkey(full_name),
          client:profiles!group_lessons_client_id_fkey(full_name, client_status)
        `)
        .gte('starts_at', startISO)
        .lt('starts_at', endISO)
        .order('starts_at', { ascending: true });

      if (lessonsErr) throw lessonsErr;

      const filtered = (lessonsData || []).filter(l =>
        !l.locations || l.locations.is_active !== false
      );

      setLessons(filtered);

      if (filtered.length > 0) {
        const ids = filtered.map(l => l.id);
        const { data: enrollData, error: enrollErr } = await supabase
          .from('lesson_enrollments')
          .select('lesson_id')
          .in('lesson_id', ids)
          .eq('status', 'enrolled');

        if (enrollErr) throw enrollErr;

        const counts = {};
        (enrollData || []).forEach(e => {
          counts[e.lesson_id] = (counts[e.lesson_id] || 0) + 1;
        });
        setEnrollmentCounts(counts);
      } else {
        setEnrollmentCounts({});
      }
    } catch (err) {
      setError(t('rooster.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [start.toISOString(), end.toISOString()]);

  useEffect(() => { loadLessons(); }, [loadLessons]);

  useEffect(() => {
    if (location.state?.toast) {
      showSuccess(location.state.toast);
    }
    if (location.state?.toast || location.state?.filterType || location.state?.trialClientId) {
      // Clear navigate state om herhaling te voorkomen bij verversen
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const days = eachDayOfInterval({ start, end });

  function lessonsForDay(day) {
    return lessons.filter(l => {
      if (typeFilter !== 'all' && l.type !== typeFilter) return false;
      const lesDate = toAmsterdamDate(l.starts_at);
      return isSameDay(lesDate, day);
    });
  }

  function handleLessonClick(les) {
    setSelectedLesson(les);
  }

  function handleModalClose() {
    setSelectedLesson(null);
  }

  function handleModalRefresh() {
    loadLessons();
    if (selectedLesson) {
      setSelectedLesson(prev => ({ ...prev }));
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('rooster.title')}</h1>
          <p className="page-subtitle">
            {t('rooster.weekVan')} {format(start, 'd MMM', { locale: nl })} {t('rooster.tm')} {format(end, 'd MMM yyyy', { locale: nl })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setWeekBase(w => subWeeks(w, 1))}>
            {t('rooster.vorigeWeek')}
          </button>
          <button className="btn btn-ghost" onClick={() => setWeekBase(new Date())}>
            {t('rooster.vandaag')}
          </button>
          <button className="btn btn-ghost" onClick={() => setWeekBase(w => addWeeks(w, 1))}>
            {t('rooster.volgendeWeek')}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/coach/les/nieuw')}>
            {t('rooster.nieuweLes')}
          </button>
        </div>
      </div>

      {trialClient && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--color-primary)11', border: '1px solid var(--color-primary)',
          borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 16, fontSize: 13,
        }}>
          <span>{t('rooster.trialBanner').replace('{naam}', trialClient.name)}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setTrialClient(null)}>
            {t('rooster.trialBannerWis')}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'group', 'personal', 'workshop'].map(f => (
          <button
            key={f}
            className={typeFilter === f ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setTypeFilter(f)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {t(`rooster.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="table-wrap">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))', gap: 12 }}>
          {days.map(day => {
            const dayLessons = lessonsForDay(day);
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()}>
                <div style={{
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 'var(--radius)',
                  background: isToday ? 'var(--color-primary)' : 'transparent',
                  color: isToday ? '#fff' : 'var(--color-text-muted)',
                  fontWeight: isToday ? 700 : 500,
                  fontSize: 12,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {format(day, 'EEE d', { locale: nl })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayLessons.length === 0 ? (
                    <div style={{
                      padding: '16px 12px',
                      background: 'var(--color-bg)',
                      borderRadius: 'var(--radius)',
                      textAlign: 'center',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                    }}>
                      —
                    </div>
                  ) : (
                    dayLessons.map(les => {
                      const enrolled = enrollmentCounts[les.id] || 0;
                      const vol = enrolled >= les.max_participants;
                      return (
                        <div
                          key={les.id}
                          className="card"
                          onClick={() => handleLessonClick(les)}
                          style={{
                            padding: '12px',
                            borderLeft: `3px solid ${les.is_cancelled ? 'var(--color-danger)' : 'var(--color-primary)'}`,
                            opacity: les.is_cancelled ? 0.6 : 1,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.15s, transform 0.1s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.boxShadow = '';
                            e.currentTarget.style.transform = '';
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                            {les.type === 'personal' && les.client?.client_status === 'prospect'
                              ? `${t('rooster.prospectPrefix')} ${les.name}`
                              : les.name}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                            {toAmsterdamTime(les.starts_at)} · {les.duration_minutes} {t('rooster.min')}
                          </div>
                          {les.description && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6, fontStyle: 'italic' }}>
                              {les.description}
                            </div>
                          )}
                          {les.locations?.name && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                              📍 {les.locations.name}
                            </div>
                          )}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            color: vol ? 'var(--color-danger)' : 'var(--color-primary)',
                          }}>
                            👤 {enrolled}/{les.max_participants}
                            {vol && <span style={{ color: 'var(--color-danger)' }}>{t('rooster.vol')}</span>}
                          </div>
                          {les.is_cancelled && (
                            <div style={{ fontSize: 11, color: 'var(--color-danger)', marginTop: 4, fontWeight: 600 }}>
                              {t('rooster.geannuleerd')}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {selectedLesson && (
        <LesDetailModal
          les={selectedLesson}
          enrollmentCount={enrollmentCounts[selectedLesson.id] || 0}
          onClose={handleModalClose}
          onRefresh={handleModalRefresh}
          prefillEnroll={trialClient ? { clientId: trialClient.id, isTrial: true } : null}
        />
      )}
    </div>
  );
}
